import { ChangeDetectionStrategy, Component, inject, output, signal } from '@angular/core';
import { FeatureFlagController } from '@domain';
import { TranslatePipe } from '@ngx-translate/core';
import { RaIconComponent } from 'src/app/shared/components/ra-icon/ra-icon.component';
import { FeedMainService } from '../../services/feed-main.service';

/**
 * Painel da direita quando NADA está selecionado — e a lista ao lado já tem itens.
 *
 * Não é a tela de primeiro uso (essa é o `order-welcome`, que ocupa a tela inteira
 * quando o feed está vazio). Aqui a pessoa já usa o produto: em vez de avisar que não
 * há nada aberto, a tela oferece o próximo passo, com a IA na frente — descrever a
 * visita é o começo mais curto, e o campo já é a ação.
 */
@Component({
    selector: 'order-empty',
    templateUrl: 'order-empty.component.html',
    styleUrl: 'order-empty.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [TranslatePipe, RaIconComponent],
})
export class OrderEmptyComponent {
    /** Texto digitado aqui, levado ao wizard "Criar com IA" (passo do contexto). */
    public startWithIa = output<string>();

    protected readonly feedMainService = inject(FeedMainService);
    /**
     * As mesmas travas do resto da tela: sem `generate_with_ia` o card da IA (que aqui é
     * o caminho principal) não existe, e sem `import_files` o card de importar some.
     */
    protected readonly featureFlagController = inject(FeatureFlagController);
    protected readonly prompt = signal('');

    protected onPromptInput(event: Event): void {
        this.prompt.set((event.target as HTMLInputElement).value);
    }

    /** Enter no campo vale como clicar em "Começar". */
    protected onPromptKey(event: KeyboardEvent): void {
        if (event.key !== 'Enter') return;

        event.preventDefault();
        this.start();
    }

    /**
     * Abre o wizard com o que já foi escrito. Sem texto também abre: o campo é um convite,
     * não uma exigência — o contexto é opcional no wizard.
     */
    protected start(): void {
        this.startWithIa.emit(this.prompt().trim());
        this.prompt.set('');
    }

    protected newService(): void {
        this.feedMainService.requestNewService();
    }

    protected newDocument(): void {
        this.feedMainService.requestNewDocument();
    }

    protected importFile(): void {
        this.feedMainService.requestImport();
    }
}
